import { useState } from "react";

import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { Uploader, UploaderProps } from "@/components/pieces/uploader";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export enum ImageFrameType {
  Circle = "circle",
  Square = "square",
}

export enum ButtonPosition {
  Top = "top",
  Bottom = "bottom",
}

interface UploadImageControllerProps {
  logoSrc: string;
  endpoint: string;
  fieldName: string;
  imagesSize?: number;
  buttonColor?: string;
  buttonLabel?: string;
  imageFrameType?: ImageFrameType;
  buttonPosition?: ButtonPosition;
  onComplete?: () => Promise<void>;
  onUploadSuccess?: UploaderProps["onUploadSuccess"];
}

export function UploadImageController({
  logoSrc,
  endpoint,
  fieldName,
  imagesSize = 120,
  buttonLabel = "Change Logo",
  buttonPosition = ButtonPosition.Bottom,
  imageFrameType = ImageFrameType.Square,
  onComplete = async () => {},
  onUploadSuccess = async (_file, _response) => {},
}: UploadImageControllerProps) {
  const [isLogoUploaderVisible, setIsLogoUploaderVisible] = useState(false);
  const isButtonTop = buttonPosition === ButtonPosition.Top;

  const handleUploadComplete = () => {
    if (onComplete) {
      onComplete().then(() => {
        setIsLogoUploaderVisible(false);
      });
    } else {
      setIsLogoUploaderVisible(false);
    }
  };

  if (isLogoUploaderVisible) {
    return (
      <div className="space-y-2 max-w-full overflow-hidden">
        <button
          onClick={() => setIsLogoUploaderVisible(false)}
          className="text-blue-600 hover:text-blue-800"
        >
          Keep current {fieldName}
        </button>
        <Uploader
          endpoint={endpoint}
          fieldName={fieldName}
          onComplete={handleUploadComplete}
          onUploadSuccess={onUploadSuccess}
        />
      </div>
    );
  }

  return (
    <div className="flex items-start gap-5">
      <LazyImg
        src={logoSrc}
        height={75}
        width={75}
        className={`w-20 h-20 object-cover ${
          imageFrameType === ImageFrameType.Circle
            ? "rounded-full"
            : "rounded-lg"
        }`}
      />
      <div
        className={cn(
          "flex-1 flex",
          isButtonTop ? "flex-col-reverse" : "flex-col",
          "gap-2",
        )}
      >
        <div
          className={cn(
            "text-sm text-muted-foreground",
            isButtonTop ? "mb-0" : "",
          )}
        >
          Square images at least {imagesSize}x{imagesSize}px
        </div>
        <Button
          intent="secondary"
          size="sm"
          className={`w-fit ${isButtonTop ? "" : ""}`}
          onClick={() => setIsLogoUploaderVisible(true)}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
